import { useSyncExternalStore } from 'react';

/**
 * The Figma desktop frames are 1440px wide and each "screen" section is 888px tall
 * (Proply web › Desktop - 7). Sections authored in those pixel coordinates are scaled uniformly to
 * fit the visitor's window, so the composition always fills the viewport the way the design does
 * instead of leaving blank bands on tall or wide windows.
 */
export const DESKTOP_DESIGN_WIDTH_PX = 1440;
export const DESKTOP_DESIGN_HEIGHT_PX = 888;

/** Never scale below/above these, so tiny or huge windows stay legible. */
const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;

/**
 * "Contain" scale: the largest uniform scale at which a `designWidth × designHeight` frame still fits
 * inside `viewportWidth × viewportHeight`.
 */
export function fitScale(
  viewportWidth: number,
  viewportHeight: number,
  designWidth = DESKTOP_DESIGN_WIDTH_PX,
  designHeight = DESKTOP_DESIGN_HEIGHT_PX,
): number {
  if (viewportWidth <= 0 || viewportHeight <= 0) return 1;
  const scale = Math.min(viewportWidth / designWidth, viewportHeight / designHeight);
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
}

/** Scale of a full-width design frame: viewport width ÷ design width (SSR snapshot 1). */
export function useWidthScale(designWidth = DESKTOP_DESIGN_WIDTH_PX): number {
  return useSyncExternalStore(
    subscribeToWindowSize,
    () => Math.round((window.innerWidth / designWidth) * 1000) / 1000,
    () => 1,
  );
}

function subscribeToWindowSize(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
}

/**
 * Current fit scale for a `designWidth × designHeight` frame, as a number that is stable between
 * renders (so it is a valid `useSyncExternalStore` snapshot). The server snapshot is 1, i.e. the
 * frame at its design size; the browser corrects it right after hydration.
 *
 * `availableHeightPx` lets a caller reserve space (for example the hero header) before fitting.
 */
export function useFitScale(
  designWidth = DESKTOP_DESIGN_WIDTH_PX,
  designHeight = DESKTOP_DESIGN_HEIGHT_PX,
  reservedHeightPx = 0,
): number {
  return useSyncExternalStore(
    subscribeToWindowSize,
    () => {
      // Round so tiny sub-pixel resizes don't re-render every frame.
      const s = fitScale(window.innerWidth, window.innerHeight - reservedHeightPx, designWidth, designHeight);
      return Math.round(s * 1000) / 1000;
    },
    () => 1,
  );
}
